import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils/cn";
import { isMockEnabled } from "@/lib/mock-flags";
import { loginWithProvider } from "../api";
import { useAuthStore } from "../stores/authStore";
import { nextRouteFor } from "../lib/postLogin";
import { isProviderConfigured, startOAuthRedirect, type Provider } from "../lib/oauth";

const providers: { id: Provider; label: string; bg: string; logo: React.ReactNode }[] = [
  {
    id: "google",
    label: "Continuar com Google",
    bg: "bg-white hover:bg-gray-50",
    logo: (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
        <path
          fill="#4285F4"
          d="M16.51 8.18c0-.51-.05-.98-.13-1.43H9v2.71h4.21c-.18.97-.74 1.79-1.58 2.34v1.95h2.55c1.49-1.37 2.34-3.4 2.34-5.57z"
        />
        <path
          fill="#34A853"
          d="M9 16c2.13 0 3.92-.71 5.23-1.92l-2.55-1.95c-.71.48-1.62.76-2.68.76-2.06 0-3.81-1.39-4.43-3.26H1.93v2.01C3.23 14.27 5.91 16 9 16z"
        />
        <path
          fill="#FBBC05"
          d="M4.57 9.63c-.16-.48-.25-.99-.25-1.51 0-.52.09-1.03.25-1.51V4.6H1.93C1.4 5.63 1.1 6.79 1.1 8.12c0 1.33.3 2.49.83 3.52L4.57 9.63z"
        />
        <path
          fill="#EA4335"
          d="M9 4.85c1.16 0 2.21.4 3.03 1.19l2.27-2.27C12.92 2.49 11.13 1.7 9 1.7 5.91 1.7 3.23 3.43 1.93 6.05L4.57 8.07C5.19 6.2 6.94 4.85 9 4.85z"
        />
      </svg>
    ),
  },
  {
    id: "microsoft",
    label: "Continuar com Microsoft",
    bg: "bg-white hover:bg-gray-50",
    logo: (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <rect x="0" y="0" width="7.6" height="7.6" fill="#F25022" />
        <rect x="8.4" y="0" width="7.6" height="7.6" fill="#7FBA00" />
        <rect x="0" y="8.4" width="7.6" height="7.6" fill="#00A4EF" />
        <rect x="8.4" y="8.4" width="7.6" height="7.6" fill="#FFB900" />
      </svg>
    ),
  },
  {
    id: "apple",
    label: "Continuar com Apple",
    bg: "bg-ink text-white hover:bg-ink/90",
    logo: (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden fill="currentColor">
        <path d="M11.6 8.5c0-1.7 1.4-2.5 1.5-2.6-0.8-1.2-2.1-1.4-2.5-1.4-1.1-0.1-2.1 0.6-2.6 0.6-0.6 0-1.4-0.6-2.3-0.6-1.2 0-2.3 0.7-2.9 1.8-1.2 2.1-0.3 5.2 0.9 6.9 0.6 0.8 1.3 1.7 2.1 1.7 0.8 0 1.2-0.5 2.3-0.5 1.1 0 1.4 0.5 2.3 0.5 1 0 1.6-0.8 2.2-1.7 0.7-1 1-1.9 1-1.9-0.1 0-2.1-0.8-2.1-3z M9.9 3.3c0.4-0.5 0.7-1.3 0.6-2-0.6 0-1.4 0.4-1.9 0.9-0.4 0.5-0.8 1.2-0.7 2 0.7 0.1 1.4-0.3 2-0.9z" />
      </svg>
    ),
  },
];

export function SocialButtons() {
  const [loading, setLoading] = useState<Provider | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();

  async function handleClick(provider: Provider) {
    setLoading(provider);
    try {
      // Provider configurado e fora do mock → fluxo real: redireciona pro consentimento
      // (PKCE). O retorno cai em /auth/callback/:provider, que abre a sessão.
      if (!isMockEnabled("auth") && isProviderConfigured(provider)) {
        await startOAuthRedirect(provider);
        return; // navegação deixa a página; mantém o loading até sair
      }

      // Sem client_id ou mock ligado → sessão direta (piloto/dev).
      const session = await loginWithProvider(provider);
      setSession(session);
      navigate(nextRouteFor(session.user), { replace: true });
    } catch {
      toast.error("Não foi possível entrar com esse provedor. Tente novamente.");
      setLoading(null);
    }
  }

  return (
    <div className="grid gap-2">
      {providers.map((p) => (
        <button
          key={p.id}
          type="button"
          disabled={loading !== null}
          onClick={() => handleClick(p.id)}
          className={cn(
            "inline-flex h-11 items-center justify-center gap-2.5 rounded-xl border border-border text-sm font-medium transition-all",
            "disabled:cursor-not-allowed disabled:opacity-60",
            p.bg,
          )}
        >
          {loading === p.id ? <IconLoader2 size={16} className="animate-spin" /> : p.logo}
          {p.label}
        </button>
      ))}
    </div>
  );
}
